import clsx from "clsx";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import {
  type AnalyticsAttrs,
  pickAnalyticsAttrs,
} from "../../../_components/ui/analytics-attrs";
import { Link } from "../../../_components/ui/link";

/**
 * アイコン専用ボタン。 touch (hit-zone) と surface (見た目) を 2 層に分けるのは、
 * タッチ領域 48px を確保しつつ視覚サイズを小さく出せるようにするため (Figma 仕様)。
 */
type DxIconButtonVariant = "solid" | "float" | "transparent";
type DxIconButtonSize = "large" | "medium" | "small" | "xsmall";

type CommonProps = AnalyticsAttrs & {
  icon: ReactNode;
  ariaLabel: string;
  variant?: DxIconButtonVariant;
  size?: DxIconButtonSize;
  className?: string;
};

type ButtonModeProps = CommonProps & {
  href?: undefined;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: ComponentPropsWithoutRef<"button">["onClick"];
};

type LinkModeProps = CommonProps & {
  href: string;
  target?: string;
  rel?: string;
};

export type DxIconButtonProps = ButtonModeProps | LinkModeProps;

const TOUCH_BASE =
  "group inline-flex items-center justify-center " +
  "focus-visible:outline-none focus:outline-none " +
  "disabled:cursor-not-allowed";

const TOUCH_SIZE: Record<DxIconButtonSize, string> = {
  large: "size-[48px]",
  medium: "size-[48px]",
  small: "h-[40px] w-[40px] min-w-[40px]",
  xsmall: "size-[32px]",
};

const SURFACE_BASE =
  "inline-flex items-center justify-center rounded-full " +
  "transition-colors duration-150 " +
  "group-focus-visible:ring-[1.5px] group-focus-visible:ring-dx-border-focus";

const SURFACE_SIZE: Record<DxIconButtonSize, string> = {
  large: "size-full",
  medium: "size-[36px]",
  small: "size-[36px]",
  xsmall: "size-full",
};

const SURFACE_VARIANT: Record<DxIconButtonVariant, string> = {
  solid:
    "bg-dx-bg-inverse text-dx-text-inverse group-hover:bg-dx-bg-inverse-hover group-disabled:bg-dx-surface-subtle group-disabled:text-dx-text-muted",
  float:
    "bg-dx-surface-raised text-dx-text-default shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_2px_6px_0_rgba(0,0,0,0.1)] group-hover:bg-dx-bg-base-hover group-disabled:bg-dx-surface-subtle group-disabled:text-dx-text-muted group-disabled:shadow-none",
  transparent:
    "bg-transparent text-dx-text-default group-hover:bg-dx-bg-base-hover group-disabled:bg-transparent group-disabled:text-dx-text-muted",
};

const ICON_SIZE: Record<DxIconButtonSize, string> = {
  large: "size-[24px]",
  medium: "size-[24px]",
  small: "size-[20px]",
  xsmall: "size-[20px]",
};

function Surface({
  variant,
  size,
  icon,
}: {
  variant: DxIconButtonVariant;
  size: DxIconButtonSize;
  icon: ReactNode;
}) {
  return (
    <span
      className={clsx(
        SURFACE_BASE,
        SURFACE_SIZE[size],
        SURFACE_VARIANT[variant],
      )}
    >
      <span
        className={clsx(
          ICON_SIZE[size],
          "inline-flex shrink-0 items-center justify-center",
        )}
      >
        {icon}
      </span>
    </span>
  );
}

export function DxIconButton(props: DxIconButtonProps) {
  const variant: DxIconButtonVariant = props.variant ?? "transparent";
  const size: DxIconButtonSize = props.size ?? "medium";
  const surface = <Surface variant={variant} size={size} icon={props.icon} />;

  if (props.href !== undefined) {
    return (
      <Link
        {...pickAnalyticsAttrs(props)}
        href={props.href}
        target={props.target}
        rel={props.rel}
        aria-label={props.ariaLabel}
        className={clsx(TOUCH_BASE, TOUCH_SIZE[size], props.className)}
      >
        {surface}
      </Link>
    );
  }
  return (
    <button
      {...pickAnalyticsAttrs(props)}
      type={props.type ?? "button"}
      disabled={props.disabled}
      onClick={props.onClick}
      aria-label={props.ariaLabel}
      className={clsx(TOUCH_BASE, TOUCH_SIZE[size], props.className)}
    >
      {surface}
    </button>
  );
}
